import { observer } from 'mobx-react-lite';
import ReactInputMask from 'react-input-mask';
import { useAddPhoneForm,useStore } from 'hooks';
import { usePhoneContacts, useSuggestedPhone } from './hooks';
import { formatPhoneByLength } from 'utils';
import { IPhone } from 'types';
import { HStack, SocialsButtonGroup,SVG, VStack } from 'components';
import { Button, Hint } from 'components/ui';
import { copyToClipboard } from 'components/ui/utils';
import { AddPhoneWrapper, ErrorRow } from './communications.styled';
import { Icons } from 'assets';

type CommunicationsProps = {
  id: string;
  phones: IPhone[];
  mail: string | null;
  withoutCallback?: boolean;
};

export const Communications = observer(
  ({ id, phones, mail }: CommunicationsProps) => {
    const { workers } = useStore();
    const {
      applicantType,
      canDelete,
      deletingPhone,
      isPhoneInList,
      persistNewPhone,
      removePhoneNumber,
    } = usePhoneContacts(id, phones);

    const addPhoneForm = useAddPhoneForm({ isPhoneInList, persistNewPhone });

    const suggestedPhone = useSuggestedPhone({
      isPhoneInList,
      persistNewPhone,
    });

    const handleAddPhone = () => {
      addPhoneForm.confirmAddPhone();

      if (applicantType === 'employee') {
        workers.invalidateDepartmentsPublic();
      }
    };

    const handleDeletePhone = (phone: IPhone) => {
      removePhoneNumber(phone);

      if (applicantType === 'employee') {
        workers.invalidateDepartmentsPublic();
      }
    };

    return (
      <HStack align="flex-start" spacing="base" wrap="wrap">
        <VStack spacing="xs">
          {phones.map((phone, index) => (
            <SocialsButtonGroup
              key={`${phone.number}-${index}`}
              phone={phone}
              withoutSocials={applicantType !== 'tenant'}
              onDelete={canDelete ? () => handleDeletePhone(phone) : undefined}
              deletePending={deletingPhone === phone.code + phone.number}
            />
          ))}

          {suggestedPhone.showSuggestedPhone && (
            <Button
              mode="neutral"
              variant="outline"
              size="s"
              pending={suggestedPhone.suggestedPending}
              onClick={suggestedPhone.addSuggestedPhone}
            >
              Добавить{' '}
              {formatPhoneByLength(suggestedPhone.suggestedPhoneDigits)}
            </Button>
          )}

          {addPhoneForm.isAdding ? (
            <VStack spacing="xxs">
              <HStack align="center" spacing="xs">
                <AddPhoneWrapper $isError={addPhoneForm.hasError}>
                  <ReactInputMask
                    mask="+7 (999) 999-99-99"
                    alwaysShowMask
                    value={addPhoneForm.newPhone}
                    onChange={addPhoneForm.changePhone}
                  />
                  <button
                    type="button"
                    onClick={addPhoneForm.clearPhone}
                    style={{
                      visibility:
                        addPhoneForm.digits.length > 0 ? 'visible' : 'hidden',
                    }}
                  >
                    <SVG src={Icons.Cross} />
                  </button>
                </AddPhoneWrapper>

                <Hint content="Сохранить" enabled={addPhoneForm.canConfirm}>
                  <Button
                    mode="success"
                    iconButton={<SVG src={Icons.Check} />}
                    size="s"
                    pending={addPhoneForm.addPending}
                    onClick={handleAddPhone}
                  />
                </Hint>
              </HStack>

              {addPhoneForm.phoneErrorText && (
                <ErrorRow>
                  <SVG src={Icons.CrossCircleFilled} />
                  {addPhoneForm.phoneErrorText}
                </ErrorRow>
              )}
            </VStack>
          ) : (
            <Button
              leftIcon={<SVG src={Icons.Plus} />}
              mode="accent"
              variant="ghost"
              size="s"
              onClick={addPhoneForm.openForm}
            >
              Добавить телефон
            </Button>
          )}
        </VStack>

        {mail && (
          <Button.Group mode="neutral" variant="minor" size="s">
            <Button onClick={() => window.open(`mailto:${mail}`, '_self')}>
              {mail}
            </Button>
            <Hint content="Скопировать">
              <Button
                iconButton={<SVG src={Icons.Clone} />}
                onClick={() =>
                  copyToClipboard(mail, undefined, {
                    success: 'Электронная почта скопирована',
                    error: 'Не удалось скопировать',
                  })
                }
              />
            </Hint>
          </Button.Group>
        )}
      </HStack>
    );
  }
);
